import React, { FC, memo, useCallback, useMemo } from 'react';
import memoize from 'memoize-one';
import { areEqual } from 'react-window';
import { pxToRem, useTheme } from '@withjoy/joykit/theme';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useTranslation } from '@shared/core';
import { CheckFilled, Import } from '@withjoy/joykit/icons';
import { PhotosCardsWrapper } from './PhotoPickerLibrary.styles';
import { PhotoCardSkeletonGroup } from '../../../../routes/ViewCollection/PhotoCardSkeletonGroup';
import { VirtualFixedGrid } from '@apps/admin/common/VirtualScroll';
import { GridItemProps, MediaPhoto, PhotoPickerLibraryProps } from '../../PhotoPickerDialog.types';
import { addRendition } from '@shared/utils/photoRendition';
import { parseInputToInt } from '@shared/utils/parseInputToInt';
import { RotatingRingLoader } from '@shared/components/RotatingRingLoader';

const GUTTER_SIZE = 12;
const CARD_HEIGHT = 172;
const ROW_GAP = 24;
const MEDIA_SKELETON = 'media-skeleton';

const createItemData = memoize(
  (
    mediaArray: MediaPhoto[],
    updateSelectedItemsId: (newSelectedItemId: string) => void,
    selectedItemsId: ReadonlyArray<string>,
    showCount: boolean,
    disabledItems: ReadonlyArray<string>,
  ) => {
    return { mediaArray, updateSelectedItemsId, selectedItemsId, showCount, disabledItems };
  },
);

const PhotoUploadingPlaceholder: FC = () => {
  const { colors } = useTheme();

  return (
    <Flex
      height={pxToRem(CARD_HEIGHT)}
      width="100%"
      borderRadius={3}
      flexDirection="column"
      backgroundColor="white"
      boxShadow="0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)"
      overflow="hidden"
      justifyContent="center"
      alignItems="center"
    >
      <Box
        position="relative"
        zIndex={1}
      >
        <RotatingRingLoader
          width="62px"
          height="62px"
          borderWidth="10px"
          borderColor="#759CFF"
          thumbColor={colors.newBrandPurple2}
        />
      </Box>
    </Flex>
  );
};

export const PhotoPickerLibrary: FC<PhotoPickerLibraryProps> = props => {
  const {
    isFetching,
    media,
    selectedItemsId,
    disabledItems,
    updateSelectedItemsId,
    handleOpenFilePicker,
    showCount = false,
    photoSelectionHelperText,
    uploadInProgressCount = 0,
  } = props;

  const { t } = useTranslation('websiteOverview');
  const photosPickerTranslationFn = t('photos', 'photoPickerDialog');

  const [columns] = useResponsive({ values: { xxs: 1, xs: 2, sm: 3, sm2: 4 } }, 4);

  const mediaPhotos: MediaPhoto[] = useMemo(
    () =>
      (media
        ?.map(m => {
          if (m.__typename === 'MediaPhoto') {
            return {
              id: m.id,
              photo: { ...m.photo, url: addRendition({ url: m.photo.url, renditionSize: 'medium' }) },
            };
          }
          return null;
        })
        .filter(media => media !== null) as MediaPhoto[]) || [],
    [media],
  );

  const uploadingPhotos = useMemo(
    () =>
      Array(uploadInProgressCount)
        .fill(null)
        .map((_, i) => ({
          id: `${MEDIA_SKELETON}-${i}`,
          photo: {
            id: `${MEDIA_SKELETON}-${i}-photo`,
            url: '',
            height: 0,
            width: 0,
          },
        })),
    [uploadInProgressCount],
  );

  const itemData = createItemData([...uploadingPhotos, ...mediaPhotos], updateSelectedItemsId, selectedItemsId, showCount, disabledItems);

  return (
    <Flex
      flexDirection="column"
      paddingX={7}
      gap={7}
    >
      <Flex
        height={pxToRem(82)}
        justifyContent="center"
        alignItems="center"
        gap={6}
        borderRadius={3}
        border="1px dashed"
        borderColor="mono4"
        backgroundColor="mono1"
      >
        <ButtonV2
          size="lg"
          variant="outline"
          shape="rounded"
          intent="neutral"
          onClick={handleOpenFilePicker}
          startIcon={<Import size={24} />}
        >
          {photosPickerTranslationFn.uploadPhotos()}
        </ButtonV2>
      </Flex>
      <Flex
        flexDirection="column"
        gap={4}
      >
        {photoSelectionHelperText && (
          <TextV2
            typographyVariant="body1"
            color="mono14"
          >
            {photoSelectionHelperText}
          </TextV2>
        )}
        {isFetching && (
          <PhotosCardsWrapper itemCount={columns * 2}>
            <PhotoCardSkeletonGroup
              loading={isFetching}
              count={columns * 2}
              height={CARD_HEIGHT}
            />
          </PhotosCardsWrapper>
        )}
        {!isFetching && itemData.mediaArray.length > 0 && (
          <VirtualFixedGrid
            itemKey={({ columnIndex, data, rowIndex }) => {
              const mediaData = (data as GridItemProps['data']).mediaArray;
              const item = mediaData[rowIndex * columns + columnIndex];
              return item?.photo.id ? item.photo.id : `${rowIndex}_${columnIndex}_element`;
            }}
            itemData={itemData}
            columnCount={columns}
            rowCount={Math.ceil(itemData.mediaArray.length / columns)}
            rowHeight={() => CARD_HEIGHT + ROW_GAP}
            overscanRowCount={4}
          >
            {GridItem}
          </VirtualFixedGrid>
        )}
      </Flex>
    </Flex>
  );
};

PhotoPickerLibrary.displayName = 'PhotoPickerLibrary';

const GridItem: React.FC<GridItemProps> = memo(({ data, style, rowIndex, columnIndex }) => {
  const { mediaArray, updateSelectedItemsId, selectedItemsId, showCount, disabledItems } = data;

  const [columns] = useResponsive({ values: { xxs: 1, xs: 2, sm: 3, sm2: 4 } }, 4);

  const shiftedIndex = rowIndex * columns + columnIndex;

  const media = mediaArray[shiftedIndex];

  const isDisabled = useMemo(() => !!media && disabledItems.includes(media.id), [disabledItems, media]);

  const handleClick = useCallback(() => {
    if (isDisabled || !media) return;
    updateSelectedItemsId(media.id);
  }, [isDisabled, media, updateSelectedItemsId]);

  if (!media) {
    return null;
  }

  const selectedItemIndex = selectedItemsId.indexOf(media.id);

  return (
    <Box
      key={media.photo.id}
      style={{
        ...style,
        // Adjust left position to account for gutters between columns
        // Formula: Original left - (total gutter space per column * current column) + (gutter size * current column)
        left: Number(style.left) - ((GUTTER_SIZE * (columns - 1)) / columns) * columnIndex + columnIndex * GUTTER_SIZE,
        // Adjust width to account for gutters between columns
        // Formula: Original width - (total gutter space per column)
        width: parseInputToInt(style.width) - (GUTTER_SIZE * (columns - 1)) / columns,
      }}
      data-row={rowIndex}
      data-col={columnIndex}
    >
      {media.id.includes(MEDIA_SKELETON) ? (
        <PhotoUploadingPlaceholder />
      ) : (
        <Flex
          key={media.id}
          position="relative"
          height={pxToRem(CARD_HEIGHT)}
          width="100%"
          borderRadius={pxToRem(12)}
          backgroundImage={`url("${media.photo.url}")`}
          backgroundSize="contain"
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          backgroundColor="white"
          boxShadow="0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)"
          overflow="hidden"
          justifyContent="center"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          onClick={handleClick}
          opacity={isDisabled ? 0.6 : 1}
        >
          <Box
            position="absolute"
            top={5}
            left={5}
            width={6}
            height={6}
          >
            {selectedItemIndex > -1 ? (
              showCount ? (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  width="100%"
                  height="100%"
                  color="white"
                  backgroundColor="mono14"
                >
                  <TextV2
                    typographyVariant="label1"
                    fontWeight={600}
                  >
                    {selectedItemIndex + 1}
                  </TextV2>
                </Flex>
              ) : (
                <Box color="mono14">
                  <CheckFilled size={24} />
                </Box>
              )
            ) : (
              <Box
                width="100%"
                height="100%"
                border="1px solid"
                borderColor="mono5"
                backgroundColor="white"
                borderRadius="full"
              />
            )}
          </Box>
          {selectedItemIndex > -1 && (
            <Box
              position="absolute"
              inset={0}
              border="4px solid"
              borderColor="mono14"
              borderRadius={pxToRem(12)}
            />
          )}
        </Flex>
      )}
    </Box>
  );
}, areEqual);
